import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { videoUrlsState } from '../../recoil';

const VideoBox: React.FC = () => {
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const liveVideoFeed = useRef<HTMLVideoElement | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<
    'recording' | 'inactive' | 'paused'
  >('inactive');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [videoChunks, setVideoChunks] = useState<Blob[]>([]);

  const [videoUrls, setVideoUrls] = useRecoilState(videoUrlsState);

  const mimeType = 'video/mp4';

  const setupVideo = async () => {
    setRecordedVideo(null);
    console.log('starting video');
    if ('MediaRecorder' in window) {
      try {
        const videoConstraints = {
          audio: false,
          video: true,
        };
        const audioConstraints = { audio: true };

        // create audio and video streams separately
        const audioStream =
          await navigator.mediaDevices.getUserMedia(audioConstraints);
        const videoStream =
          await navigator.mediaDevices.getUserMedia(videoConstraints);

        //combine both audio and video streams
        const combinedStream = new MediaStream([
          ...videoStream.getVideoTracks(),
          ...audioStream.getAudioTracks(),
        ]);
        setStream(combinedStream);

        //set videostream to live feed player
        if (liveVideoFeed.current) {
          liveVideoFeed.current.srcObject = videoStream;
        }
      } catch (err: any) {
        alert(err.message);
      }
    } else {
      alert('The MediaRecorder API is not supported in your browser.');
    }
  };

  useEffect(() => {
    setupVideo();
  }, []);

  const startRecording = async () => {
    setupVideo();
    if (stream) {
      setRecordingStatus('recording');
      const media = new MediaRecorder(stream, { mimeType });
      mediaRecorder.current = media;
      mediaRecorder.current.start();

      let localVideoChunks: Blob[] = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (typeof event.data === 'undefined') return;
        if (event.data.size === 0) return;
        localVideoChunks.push(event.data);
        console.log(`Chunk received: size ${event.data.size}`);
      };

      setVideoChunks(localVideoChunks);

      mediaRecorder.current.onerror = (error) => {
        console.error(`MediaRecorder error: ${error}`);
      };
    } else {
      console.log('there is no stream');
    }
  };

  const stopRecording = () => {
    setRecordingStatus('inactive');
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();

      mediaRecorder.current.onstop = async () => {
        console.log('Recording stopped and data collected');

        // turn chunks into a single video
        const videoBlob = new Blob(videoChunks, { type: mimeType });
        const videoUrl = URL.createObjectURL(videoBlob);

        // set video to recorded video and empty chunks
        setRecordedVideo(videoUrl);
        setVideoChunks([]);

        // API call to upload video
        const UPLOAD_URL =
          'https://disney-multimodal-video-gateway-no91dcy.uc.gateway.dev/geminiText';

        // create form data to send video blob
        const data = new FormData();
        let videoId = uuidv4();
        const videoUrlmp4 = `${videoId}.mp4`;

        data.append('file', videoBlob, videoUrlmp4);

        const response = await fetch(UPLOAD_URL, {
          method: 'POST',
          // omit headers to let browser set it, in particular the Content-Type header to include the boundary
          body: data,
        });

        console.log(response);
        const responseText = await response.text();
        console.log(responseText);

        const gsUrl = `gs://disney-multimodal-ai-content/${videoId}.mp4`;
        setVideoUrls([gsUrl]);
      };
    } else {
      console.log('there is no media recorder');
    }
  };

  return (
    <div className="flex w-1/2 flex-col">
      <div className="video-player">
        {!recordedVideo ? (
          <video
            className="w-full rounded-xl"
            ref={liveVideoFeed}
            autoPlay
          ></video>
        ) : null}
        {recordedVideo ? (
          <div className="recorded-player">
            <video
              className="w-full rounded-xl"
              src={recordedVideo}
              controls
            ></video>
            {/* <a download href={recordedVideo}>
              Download Recording
            </a> */}
          </div>
        ) : null}
      </div>
      {recordingStatus === 'inactive' ? (
        <button
          onClick={startRecording}
          type="button"
          className="my-2 cursor-pointer rounded-xl bg-blue-200 px-4 py-2 font-bold text-gray-700"
        >
          Start Recording
        </button>
      ) : null}
      {recordingStatus === 'recording' ? (
        <button
          onClick={stopRecording}
          type="button"
          className="my-2 rounded-lg bg-red-300 px-4 py-2 font-bold text-gray-700"
        >
          Stop Recording
        </button>
      ) : null}
    </div>
  );
};

export default VideoBox;
